import cloneDeep from 'lodash/cloneDeep';

import {serializeFilters} from 'nutshell-core';

import {Routing} from 'nutshell-core/routing';
import type {SavedFilter, SavedReportType, ReportFacet, FilterObject} from 'nutshell-core/types';

import type {SortBy} from 'shells/table/types';

import {getReportDefaultState} from './report-constants';
import type {
    ReportKey,
    ReportGap,
    ReportSegment,
    DateRangeType,
    ChartReportParams,
    SortParams,
} from './report-types';

export type ReportPostParams = {
    filter: any; // Filter object,
    groupByField: DateRangeType | null | undefined;
    pivotFacet: ReportFacet | null | undefined;
    reportType: ReportKey;
    gap: ReportGap;
    useConfidence: string | null | undefined;
    segmentByField: ReportSegment | null | undefined;
    excludeEmpty?: boolean;
};

export function getCustomizeLinkForReportFromSearch(search: string) {
    return `/leads${search}&view=explore`;
}

export function getPostParams(
    reportKey: ReportKey,
    reportParams: ChartReportParams,
    filters: Array<FilterObject>,
    format?: string | null
): ReportPostParams {
    const {gap, facet, segment, useConfidence, dateRangeType} = reportParams;

    return {
        filter: serializeFilters(filters),
        groupByField: dateRangeType,
        pivotFacet: facet,
        reportType: reportKey,
        gap,
        useConfidence,
        segmentByField: segment,
        // @ts-expect-error
        format,
    };
}

/**
 * Strips out urlParams that we don't want to save to the server. For example,
 * it's necessary to have the `report.id` in the url, but we don't actually
 * want that to be saved to the `value` column in the database
 *
 * @param  {object} urlParams  - urlParams object from url to strip report.id and list.page
 * @return {string}            - Param'd urlString that does not contain blacklisted params
 */
export function getSafeUrlParams(urlParams: any): string {
    const safeParams = cloneDeep(urlParams);
    if (safeParams.report) {
        delete safeParams.report.id;
    }

    if (safeParams.list) {
        delete safeParams.list.page;
    }

    return Routing.param(safeParams);
}

/**
 * Check our current report's value with either the default state of the
 * current report (i.e. the default `Sales` report url), or the persisted
 * saved value of the report.
 *
 * This method is slightly complicated because the URL doesn't match up
 * exactly with the saved value of the report, since we purposefully remove
 * both the report id and the lists' page param when saving to the DB, so
 * we can't just liberally compare the url with the report's value
 *
 * @param  {string}  reportKey     - Unique report key
 * @param  {Object}  urlParams     - Entire urlParam object to check dirtyness
 * @param  {Object}  savedReport   - Are we comparing the dirtyness of saved report?
 *
 * @return {boolean} Is this report dirty?
 */
export function isReportDirty(
    reportKey: ReportKey,
    urlParams: any,
    savedReport?: SavedFilter<SavedReportType> | null
) {
    const currentParams = getSafeUrlParams(urlParams);

    if (savedReport) {
        return currentParams !== savedReport.value || Boolean(savedReport.isHidden);
    }

    const defaultReportState = getReportDefaultState(reportKey);

    const urlParamString = Routing.deparam(defaultReportState.search.slice(1));
    const safeUrlParamString = getSafeUrlParams(urlParamString);

    return currentParams !== safeUrlParamString;
}

export function convertSortParamsToSortBy(sortParams: SortParams): SortBy[] {
    return [
        {
            id: sortParams.by,
            // @ts-expect-error
            desc: sortParams.direction === 'desc',
        },
    ];
}

export function convertSortStringToSortParams(sortString: string): SortParams {
    return {
        by: sortString,
        direction: sortString.startsWith('-') ? 'des' : 'asc',
    };
}

/**
 * Calculates the total or average count for a given segment across all rows in a report table.
 *
 * @param {Object[]} rows - The table rows to process
 * @param {string} segmentName - The segment name to find and process
 * @param {string} [columnId] - The column ID to look up values from. If not provided, uses segmentName
 * @param {Function} [rowFilter] - Optional filter function to determine which rows to include
 * @return {string} The formatted result as a locale string
 */
export function getReportSegmentAggregate(
    rows: any[],
    segmentName: string,
    columnId?: string,
    rowFilter?: (row: any) => boolean
): string {
    const lookupColumnId = columnId || segmentName;
    const filteredRows = rows.filter((row) => !rowFilter || rowFilter(row));

    const total = filteredRows.reduce((acc, row) => {
        const rowValues = row.values[lookupColumnId];
        if (rowValues) {
            // @ts-expect-error
            const segment = rowValues.segments.find((seg) => seg.title === segmentName);
            const segmentValue =
                segment &&
                segment.totalsByReport &&
                segment.totalsByReport[0] &&
                segment.totalsByReport[0].count &&
                segment.totalsByReport[0].count.value
                    ? segment.totalsByReport[0].count.value
                    : 0;

            return acc + segmentValue;
        }

        return acc;
    }, 0);

    return total.toLocaleString('en-US');
}
